// Shared labels + types for Account Intelligence signals (Feature #39, M2).

export type SignalStatus = "pending" | "approved" | "rejected"
export type SignalOrigin = "auto_research" | "pic_entry"

export type Signal = {
  id: number
  domain: string
  aspect: string | null
  title: string
  body: string | null
  source_url: string | null
  source_status: string | null
  source_date: string | null
  event_month: string | null
  geo_place: string | null
  polarity: string | null
  horizon: string | null
  impact_axis: string | null
  magnitude: number | null
  confidence: number | null
  is_structural: boolean
  origin: SignalOrigin
  status: SignalStatus
  reviewed_by_name: string | null
  reviewed_at: string | null
}

export type SignalTaxonomy = {
  domains: string[]
  aspects_by_domain: Record<string, string[]>
  polarities: string[]
  horizons: string[]
  impact_axes: string[]
}

// --- Scores & alerts (M3) ---

export type AxisScore = { axis: string; score: number }

export type PolarityScore = {
  total: number
  count: number
  by_axis: AxisScore[]
}

export type AccountScores = {
  opportunity: PolarityScore
  risk: PolarityScore
}

export type AlertTone = "info" | "success" | "warning" | "danger"

export type CombinationAlert = {
  key: string
  name: string
  tone: AlertTone
  polarity: string
  description: string
  signal_ids: number[]
  matched_count: number
}

// --- Portfolio segments (M4) ---

export type PortfolioSegment =
  | "critical"
  | "high_opportunity"
  | "high_risk"
  | "watch"
  | "no_data"

export const SEGMENT_META: Record<
  PortfolioSegment,
  { label: string; tone: BadgeTone; symbol: string }
> = {
  critical: { label: "Kritis", tone: "danger", symbol: "⚑" },
  high_opportunity: { label: "Peluang Tinggi", tone: "success", symbol: "★" },
  high_risk: { label: "Risiko Tinggi", tone: "danger", symbol: "⚠" },
  watch: { label: "Pantau", tone: "neutral", symbol: "•" },
  no_data: { label: "Belum ada sinyal", tone: "muted", symbol: "–" },
}

export function segmentMeta(segment: string) {
  return SEGMENT_META[(segment as PortfolioSegment)] ?? SEGMENT_META.no_data
}

export const UNASSIGNED_AXIS = "unassigned"

export function impactAxisLabel(axis: string): string {
  if (axis === UNASSIGNED_AXIS) return "Tanpa sumbu dampak"
  return IMPACT_AXIS_LABELS[axis] ?? humanize(axis)
}

export const DOMAIN_LABELS: Record<string, string> = {
  corporate_governance: "Tata Kelola Korporat",
  people_organization: "Orang & Organisasi",
  network_expansion: "Ekspansi Jaringan",
  supply_chain_operations: "Rantai Pasok & Operasi",
  category_merchandising: "Kategori & Merchandising",
  pricing_promotion: "Harga & Promosi",
  digital_ecom_omnichannel: "Digital & Omnichannel",
  financial_health_credit: "Kesehatan Finansial & Kredit",
  competition_market_position: "Kompetisi & Posisi Pasar",
  regulatory_political: "Regulasi & Politik",
  consumer_shopper: "Konsumen & Shopper",
  reputation_crisis: "Reputasi & Krisis",
}

export const POLARITY_LABELS: Record<string, string> = {
  opportunity: "Peluang",
  threat: "Ancaman",
  neutral: "Netral",
}

export const HORIZON_LABELS: Record<string, string> = {
  descriptive: "Deskriptif",
  leading: "Indikator awal",
}

export const IMPACT_AXIS_LABELS: Record<string, string> = {
  revenue: "Pendapatan",
  margin: "Margin",
  distribution: "Distribusi",
  credit_risk: "Risiko Kredit",
  relationship: "Relasi",
}

export type BadgeTone =
  | "neutral"
  | "accent"
  | "signal"
  | "muted"
  | "solid"
  | "success"
  | "danger"

export function polarityTone(polarity: string | null): BadgeTone {
  switch (polarity) {
    case "opportunity":
      return "success"
    case "threat":
      return "danger"
    default:
      return "muted"
  }
}

export function domainLabel(domain: string): string {
  return DOMAIN_LABELS[domain] ?? humanize(domain)
}

export function aspectLabel(aspect: string | null): string | null {
  if (!aspect) return null
  return humanize(aspect)
}

export function humanize(value: string): string {
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export const OTHER_ASPECT = "__other__"
